import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import RouteViewComponent from '../layouts/RouterBypass.vue'
import api from '../services/api'
import { useToast } from 'vuestic-ui'
import { isChunkLoadingError, recoverFromChunkError, resetChunkReloadGuard } from '../utils/chunkRecovery'
import { getAuthContext } from '../services/authContext'
import { canRefreshActiveToken, forceLogout, clearAuthSession, isTokenExpired } from '../services/forceLogout'
import { refreshActiveToken } from '../services/tokenRefresh'
import {
  getCachedAuthValidation,
  invalidateAuthCache,
  setAuthValidationCache,
} from '../services/authValidationCache'
import { prefetchUsersTab } from '../pages/users_tab/Users_Tab'

export { invalidateAuthCache }

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: () => {
      const { isAgentContext, role } = getAuthContext()

      if (isAgentContext) {
        return '/agent/agents-management'
      }

      const currentRole = role || localStorage.getItem('role') || 'admin'

      if (currentRole === 'support') {
        return '/admin/chat-conversations'
      }

      return '/admin/dashboard'
    },
  },
  {
    name: 'admin',
    path: '/admin',
    component: AppLayout,
    meta: { requiresAuth: true, role: 'admin' },
    children: [
      {
        name: 'price-config',
        path: 'price-config',
        component: () => import('../pages/price/PriceConfigPage.vue'),
      },
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'system-settings',
        path: 'system-settings',
        component: () => import('../pages/system-settings/SystemSettingsPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'super_admin'],
        },
      },
      {
        name: 'purchase_history',
        path: 'purchase_history',
        component: () => import('../components/Payement/PurchaseHistory.vue'),
      },
      {
        name: 'steal_stat',
        path: 'steal_stat',
        component: () => import('../pages/steal-stat/StealStat.vue'),
      },
      {
        name: 'orders',
        path: 'orders',
        component: () => import('../pages/orders/OrderManagementPage.vue'),
      },
      {
        name: 'statistique',
        path: 'statistique',
        component: () => import('../components/Payement/Paymentstatitem.vue'),
      },
      {
        name: 'announcements',
        path: 'announcements',
        component: () => import('../pages/announcements/AnnouncementsPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin'],
        },
      },
      {
        name: 'users_tab',
        path: 'users_tab',
        component: () => import('../pages/users_tab/Users_Tab.vue'),
        beforeEnter: () => {
          prefetchUsersTab()
        },
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
      },


      {
        name: 'chat-conversations',
        path: 'chat-conversations',
        component: () => import('../pages/chat_conversation/ChatConversation.vue'),
      },
      {
        name: 'chat-log',
        path: 'chat-log',
        component: () => import('../pages/chat-log/ChatLog.vue'),
      },
      {
        name: 'user-notification',
        path: 'user-notification',
        component: () => import('../pages/user_notification/UserNotification.vue'),
      },
      {
        name: 'versions',
        path: 'versions',
        component: () => import('../pages/versions/VersionsPage.vue'),
      },
      {
        name: 'versions-link',
        path: 'versions-link',
        component: () => import('../pages/versions/VersionLink.vue'),
      },
      {
        path: 'videos',
        component: RouteViewComponent,
        children: [
          {
            name: 'videos',
            path: '',
            component: () => import('../pages/videos/VideoPage.vue'),
          },
          {
            name: 'category',
            path: 'category',
            component: () => import('../pages/category/CategoryPage.vue'),
          },
          {
            name: 'category_p',
            path: 'category_p',
            component: () => import('../pages/categorypost/CategoryPost.vue'),
          },
          {
            name: 'tag',
            path: 'tag',
            component: () => import('../pages/tag/Tag.vue'),
          },
        ],
      },
      {
        name: 'post',
        path: 'post',
        component: () => import('../pages/post/PostPage.vue'),
      },


      {
        name: 'coming_soon',
        path: 'coming_soon',
        component: () => import('../pages/coming_soon/ComingSoon.vue'),
      },
      {
        name: 'tag_p',
        path: 'tag_p',
        component: () => import('../pages/tagpost/TagPost.vue'),
      },
      {
        name: 'banner',
        path: 'banner',
        component: () => import('../pages/banner/Banner.vue'),
      },
      // {
      //   name: 'ads',
      //   path: 'ads',
      //   component: () => import('../pages/ads/AdsPage.vue'),
      //   meta: {
      //     requiresAuth: true,
      //   },
      // },
      {
        name: 'ads',
        path: 'ads',
        component: () => import('../pages/ads/tabs/AdsTab.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin'],

        },
      },
      {
        name: 'video-ads',
        path: 'video-ads',
        component: () => import('../pages/ads/tabs/VideoAdsTab.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'video-durations',
        path: 'video-durations',
        component: () => import('../pages/ads/tabs/VideoAdDurationsTab.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'activities',
        path: 'activities',
        component: () => import('../pages/ads/tabs/AdActivitiesTab.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'collections',
        path: 'collections',
        component: () => import('../pages/ads/tabs/AdCollectionsTab.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'placements',
        path: 'placements',
        component: () => import('../pages/ads/tabs/AdPlacementsTab.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'switches',
        path: 'switches',
        component: () => import('../pages/ads/tabs/SwitchControlsTab.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'triggers',
        path: 'triggers',
        component: () => import('../pages/ads/tabs/BehaviorTriggersTab.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'ads-settings',
        path: 'ads-settings',
        component: () => import('../pages/ads/tabs/AdSettingsTab.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'tracking',
        path: 'tracking',
        component: () => import('../pages/ads/tabs/AdTrackingTab.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'statistics',
        path: 'statistics',
        component: () => import('../pages/ads/tabs/AdStatisticsTab.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'targeting',
        path: 'targeting',
        component: () => import('../pages/ads/tabs/AdTargetingTab.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'ads-agents',
        path: 'ads-agents',
        component: () => import('../pages/ads/tabs/AdAgentsTab.vue'),
        meta: { requiresAuth: true },
      },

      {
        name: 'emoji',
        path: 'emoji',
        component: () => import('../pages/emoji/EmojiPage.vue'),
      },
      {
        name: 'translate',
        path: 'translate',
        component: () => import('../pages/gestionTraduction/GestionTraduction.vue'),
      },
      {
        name: 'admin-login-history',
        path: 'login-history',
        component: () => import('../pages/admin-login-history/AdminLoginHistoryPage.vue'),
        meta: { requiresAuth: true, allowedRoles: ['admin', 'super_admin'] },
      },
      {
        path: 'game',
        component: RouteViewComponent,
        children: [
          // {
          //   name: 'game-history',
          //   path: 'history',
          //   component: () => import('../pages/game-management/GameHistoryPage.vue'),
          // },
          {
            name: 'game-reduce',
            path: 'reduce',
            component: () => import('../pages/game-management/GameReducerPage.vue'),
          },
          {
            name: 'game-catalog',
            path: 'catalog',
            component: () => import('../pages/game-management/GameCatalogPage.vue'),
          },
          {
            name: 'game-type',
            path: 'type',
            component: () => import('../pages/game-management/GameTypePage.vue'),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin'],
            },
          },
          {
            name: 'game-platforms',
            path: 'platforms',
            component: () => import('../pages/game-management/GamePlatformsPage.vue'),
          },
          {
            name: 'game-fund-transfers',
            path: 'fund-transfers',
            component: () => import('../pages/game-management/GameFundTransfersPage.vue'),
          },
          {
            name: 'game-country-mapping',
            path: 'country-mapping',
            component: RouteViewComponent,
            children: [
              {
                name: 'game-platform-i18n',
                path: 'platform-i18n',
                component: () => import('../pages/game-management/GameCountryMappingPage.vue'),
              },
              {
                name: 'game-internationalization',
                path: 'internationalization',
                component: () => import('../pages/game-management/GameInternationalizationPage.vue'),
              },
            ],
          },
        ],
      },
      {
        name: 'country',
        path: 'country',
        component: () => import('../pages/country/CountryPage.vue'),
      },
      {
        name: 'domain_name_registration',
        path: 'domain_name_registration',
        component: () => import('../pages/Domain_name_registration/DomainNameRegistration.vue'),
      },

      {
        name: 'domain',
        path: 'domain',
        component: RouteViewComponent,
        children: [
          {
            name: 'domain-management',
            path: '',
            component: () => import('../pages/domain/DomainPage.vue'),
          },
          {
            name: 'domain-surveillance',
            path: 'surveillance',
            component: () => import('../pages/domain/DomainSurveillancePage.vue'),
          },
          {
            name: 'domain-mapping',
            path: 'mapping',
            component: () => import('../pages/domain/DomainMappingPage.vue'),
          },
          {
            name: 'domain-failover',
            path: 'failover',
            component: () => import('../pages/domain/FailoverLogsPage.vue'),
          },
          {
            name: 'domain-api-change-logs',
            path: 'api-change-logs',
            component: () => import('../pages/domain/ApiDomainChangeLogsPage.vue'),
          },
          {
            name: 'domain-resource-change-logs',
            path: 'resource-change-logs',
            component: () => import('../pages/domain/ResourceDomainChangeLogsPage.vue'),
          },
        ],
      },
      {
        name: 'config',
        path: 'config',
        component: () => import('../pages/config/ConfigPage.vue'),
      },
      {
        name: 'download',
        path: 'download',
        component: () => import('../pages/download/DownloadPage.vue'),
      },
      {
        name: 'promotions',
        path: 'promotions',
        component: () => import('../pages/promotions/PromotionPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent'],
        },
      },
      {
        name: 'landing-page',
        path: 'landing-page',
        component: () => import('../pages/landing-page/LandingPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent'],
        },
      },
      {
        name: 'annonce-editor',
        path: 'annonce-editor',
        component: () => import('../pages/announce_editor/AnnouceEditor.vue'),
      },
      {
        name: 'annonce-editor_dark',
        path: 'annonce-editor-dark',
        component: () => import('../pages/announce_editor_dark/Announce_edito_dark.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin'],
        },
      },
      {
        name: 'create-promotions',
        path: 'promotions/create',
        component: () => import('../pages/promotions/CreatePromotions.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent'],
        },
      },
      {
        name: 'promotion-platform',
        path: 'promotion-platform',
        component: () => import('../pages/promotions/Promotion-platform.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent'],
        },
      },
      {
        name: 'promotion-link',
        path: 'promotion-link',
        component: () => import('../pages/promotions/PromotionLink.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin'],
        },
      },
      {
        name: 'promotion-tracking',
        path: 'promotion-tracking',
        component: () => import('../pages/promotions/PromotionTracking.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent', 'super_admin'],
        },
      },
      //Landing page
      {
        path: 'landing',
        component: RouteViewComponent,
        children: [
          {
            name: '',
            path: '',
            component: () => import('../pages/landing-page/LandingPage.vue'),
          },
          {
            name: 'landing-management',
            path: 'management-list',
            component: () => import('../pages/landing-page/GestionLandingPage.vue'),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin'],
            },
          },
          {
            name: 'landing-tag',
            path: 'tag',
            component: () => import('../pages/tag/Tag.vue'),
          },
        ],
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'profile',
        path: 'profile',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'faq',
        path: 'faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
      // VIP Management
      {
        path: 'vip',
        component: RouteViewComponent,
        children: [
          {
            name: 'vip',
            path: '',
            component: () => import('../pages/vip/VipPlansPage.vue'),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin', 'agent'],
            },
          },

          {
            name: 'vip-setting',
            path: 'vip-setting',
            component: () => import('../pages/vip/VipSettingPage.vue'),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin', 'agent'],
            },
          },
          {
            name: 'movie_price',
            path: 'movie_price',
            component: () => import('../pages/vip/MoviePricePage.vue'),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin', 'agent'],
            },
          },
          {
            name: 'vip-rights',
            path: 'rights',
            component: () => import('../pages/vip/VipRightsPage.vue'),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin'],
            },
          },
        ],
      },
      // Agents VIP Management
      {
        name: 'agents-vip',
        path: 'agents-vip',
        component: () => import('../pages/agents/AgentsVipPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['agent', 'admin'],
        },
      },
      {
        name: 'users-vip',
        path: 'users-vip',
        component: () => import('../pages/vip/VipUsersPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent'],
        },
      },
      {
        name: 'wallet-management',
        path: 'wallet-management',
        component: () => import('../pages/wallet/WalletManagementPage.vue'),
      },
      {
        name: 'withdraw',
        path: 'withdraw',
        component: () => import('../pages/wallet/WithdrawPage.vue'),
      },
      {
        name: 'admin-agent-withdraw',
        path: 'agent-withdraw',
        component: () => import('../pages/wallet/AgentWithdrawPage.vue'),
      },
      {
        name: 'withdraw-user',
        path: 'withdraw-user',
        component: () => import('../pages/wallet/WithdrawhistoryPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'super_admin'],
        },
      },
      {
        name: 'user-withdraw',
        path: 'user-withdraw',
        component: () => import('../pages/wallet/UserWithdrawPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'super_admin'],
        },
      },
      // Agents Comissions Management
      {
        path: 'agents-management',
        component: RouteViewComponent,
        children: [
          {
            name: 'agents-management',
            path: '',
            component: () => import('../pages/agent-management/AgentsManagementPage.vue'),
            meta: {
              requiresAuth: true,
            },
          },
          {
            name: 'agents-commission',
            path: 'commission',
            component: () => import('../pages/agent-management/AgentsCommission.vue'),
            meta: {
              requiresAuth: true,
            },
          },
          {
            name: 'agents-incomes',
            path: 'incomes',
            component: () => import('../pages/agent-management/AgentIncomesPage.vue'),
            meta: {
              requiresAuth: true,
            },
          },
          {
            name: 'agents-revenue',
            path: 'revenue',
            component: () => import('../pages/agent_revenue/AgentRevenue.vue'),
            meta: {
              requiresAuth: true,
            },
          },
        ],
      },
      //Exchange page
      {
        path: 'exchange',
        component: RouteViewComponent,
        children: [
          {
            name: 'exchange-plans',
            path: '',
            component: () => import('../pages/exchange/ExchangePage.vue'),
          },
          {
            name: 'payment-platforms',
            path: 'payment-platforms',
            component: () => import('../pages/payment-platform/PaymentPlatformsPage.vue'),
          },
          {
            name: 'payment-channels',
            path: 'payment-channels',
            component: () => import('../pages/payment-platform/PaymentChannelsPage.vue'),
          },
        ],
      },
      {
        path: '/mangas',
        component: () => import('../pages/mangas/views/MangasPage.vue'),
        name: 'mangas',
      },
    ],
  },
  {
    name: 'agent',
    path: '/agent',
    component: AppLayout,
    meta: { requiresAuth: true, role: 'agent' },
    children: [
      {
        name: 'agent-dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'agent-users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
      },
      {
        name: 'agent-promotion-link',
        path: 'promotion-link',
        component: () => import('../pages/promotions/PromotionLink.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent'],
        },
      },
      {
        name: 'agent-annonce-editor',
        path: 'annonce-editor',
        component: () => import('../pages/announce_editor/AnnouceEditor.vue'),
      },
      {
        name: 'agent-purchase_history',
        path: 'purchase_history',
        component: () => import('../components/Payement/PurchaseHistory.vue'),
      },
      {
        name: 'agent-statistique',
        path: 'statistique',
        component: () => import('../components/Payement/Paymentstatitem.vue'),
      },
      {
        name: 'agent-promotions',
        path: 'promotions',
        component: () => import('../pages/promotions/PromotionPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent'],
        },
      },
      {
        name: 'agent-domain_name_registration',
        path: 'domain_name_registration',
        component: () => import('../pages/Domain_name_registration/DomainNameRegistration.vue'),
      },
      {
        name: 'agent-landing-page',
        path: 'landing-page',
        component: () => import('../pages/landing-page/LandingPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent'],
        },
      },
      {
        name: 'agent-create-promotions',
        path: 'promotions/create',
        component: () => import('../pages/promotions/CreatePromotions.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent'],
        },
      },
      {
        name: 'agent-promotion-platform',
        path: 'promotion-platform',
        component: () => import('../pages/promotions/Promotion-platform.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent'],
        },
      },
      {
        name: 'agent-promotion-tracking',
        path: 'promotion-tracking',
        component: () => import('../pages/promotions/PromotionTracking.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent', 'super_admin'],
        },
      },
      {
        path: 'landing',
        component: RouteViewComponent,
        children: [
          {
            name: 'agent-landing',
            path: '',
            component: () => import('../pages/landing-page/LandingPage.vue'),
          },
          {
            name: 'agent-tag',
            path: 'tag',
            component: () => import('../pages/tag/Tag.vue'),
          },
        ],
      },
      {
        name: 'agent-settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'agent-preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'agent-profile',
        path: 'profile',
        component: () => import('../pages/preferences/Preferences.vue'),
      },

      {
        path: 'vip',
        component: RouteViewComponent,
        children: [
          {
            name: 'agent-vip',
            path: '',
            component: () => import('../pages/vip/VipPlansPage.vue'),
            meta: {
              requiresAuth: true,
              allowedRoles: ['agent', 'admin'],
            },
          },
          {
            name: 'agent-movie-price',
            path: 'movie_price',
            component: () => import('../pages/vip/MoviePricePage.vue'),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin', 'agent'],
            },
          },
          {
            name: 'agent-vip-setting',
            path: 'vip-setting',
            component: () => import('../pages/vip/VipSettingPage.vue'),
            meta: {
              requiresAuth: true,
              allowedRoles: ['agent', 'admin'],
            },
          },
          {
            name: 'agent-vip-rights',
            path: 'rights',
            component: () => import('../pages/vip/VipRightsPage.vue'),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin'],
            },
          },
        ],
      },
      {
        name: 'agent-agents-vip',
        path: 'agents-vip',
        component: () => import('../pages/agents/AgentsVipPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['agent', 'admin'],
        },
      },
      {
        name: 'agent-orders',
        path: 'orders',
        component: () => import('../pages/orders/OrderManagementPage.vue'),
      },
      {
        name: 'agent-users-vip',
        path: 'users-vip',
        component: () => import('../pages/vip/VipUsersPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['agent', 'admin'],
        },
      },
      {
        name: 'agent-wallet-management',
        path: 'wallet-management',
        component: () => import('../pages/wallet/WalletManagementPage.vue'),
      },
      {
        name: 'agent-withdraw',
        path: 'withdraw',
        component: () => import('../pages/wallet/AgentWithdrawPage.vue'),
      },
      {
        name: 'agent-chat-conversations',
        path: 'chat-conversations',
        component: () => import('../pages/chat_conversation/ChatConversation.vue'),
      },
      {
        name: 'agent-users_tab',
        path: 'users_tab',
        component: () => import('../pages/users_tab/Users_Tab.vue'),
        beforeEnter: () => {
          prefetchUsersTab()
        },
      },
      {
        name: 'agent-price-config',
        path: 'price-config',
        component: () => import('../pages/price/PriceConfigPage.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'agents-management',
        component: RouteViewComponent,
        children: [
          {
            name: 'agent-agents-management',
            path: '',
            component: () => import('../pages/agent-management/AgentsManagementPage.vue'),
            meta: {
              requiresAuth: true,
            },
          },
          {
            name: 'agent-agents-commission',
            path: 'commission',
            component: () => import('../pages/agent-management/AgentsCommission.vue'),
            meta: {
              requiresAuth: true,
            },
          },
          {
            name: 'agent-agents-incomes',
            path: 'incomes',
            component: () => import('../pages/agent-management/AgentIncomesPage.vue'),
            meta: {
              requiresAuth: true,
            },
          },
          {
            name: 'agent-agents-revenue',
            path: 'revenue',
            component: () => import('../pages/agent_revenue/AgentRevenue.vue'),
            meta: {
              requiresAuth: true,
            },
          },
        ],
      },
      {
        path: 'videos',
        component: RouteViewComponent,
        children: [
          {
            name: 'agent-videos',
            path: '',
            component: () => import('../pages/videos/VideoPage.vue'),
          },
          {
            name: 'agent-category',
            path: 'category',
            component: () => import('../pages/category/CategoryPage.vue'),
          },
          {
            name: 'agent-category_p',
            path: 'category_p',
            component: () => import('../pages/categorypost/CategoryPost.vue'),
          },
          {
            name: 'agent-tag',
            path: 'tag',
            component: () => import('../pages/tag/Tag.vue'),
          },
        ],
      },
      {
        name: 'agent-post',
        path: 'post',
        component: () => import('../pages/post/PostPage.vue'),
      },

      {
        name: 'agent-announcements',
        path: 'announcements',
        component: () => import('../pages/announcements/AgentAnnouncementsPage.vue'),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'agent'],
        },
      },
    ],
  },
  {
    path: '/auth',
    name: 'auth',
    component: AuthLayout,
    children: [
      {
        name: 'token-auth',
        path: '',
        component: () => import('../pages/auth/TokenAuth.vue'),
      },
      {
        name: 'index',
        path: '/',
        redirect: { name: 'login' },
      },
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      // {
      //   name: 'signup',
      //   path: 'signup',
      //   component: () => import('../pages/auth/Signup.vue'),
      // },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        name: 'logout',
        path: 'logout',
        component: () => import('../pages/auth/Logout.vue'),
      },
      {
        name: 'agent-logout',
        path: 'agent-logout',
        component: () => import('../pages/auth/AgentLogout.vue'),
      },
    ],
  },
  {
    name: 'impersonate',
    path: '/impersonate',
    component: () => import('../pages/impersonation/ImpersonatePage.vue'),
    meta: { requiresAuth: false },
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const { init: notify } = useToast()

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.afterEach(() => {
  resetChunkReloadGuard()
})

router.onError((error) => {
  if (isChunkLoadingError(error)) {
    const recovered = recoverFromChunkError()
    if (!recovered) {
      console.error('Chunk loading error persisted after reload:', error)
    }
    return
  }

  console.error('Router navigation error:', error)
})

const getAuthenticatedAdmin = async (tokenAdmin: string) => {
  if (!tokenAdmin) return false

  if (isTokenExpired(tokenAdmin)) {
    if (!canRefreshActiveToken()) {
      return false
    }

    const refreshedToken = await refreshActiveToken()
    if (!refreshedToken) {
      return false
    }

    tokenAdmin = refreshedToken
  }

  const cached = getCachedAuthValidation(tokenAdmin)
  if (cached !== null) {
    return cached
  }

  try {
    const response = await fetch(api.getAuth(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenAdmin}`,
      },
    })

    setAuthValidationCache(tokenAdmin, response.ok)
    return response.ok
  } catch {
    setAuthValidationCache(tokenAdmin, false)
    return false
  }
}

router.beforeEach(async (to, _from, next) => {
  // Allow impersonation and auth routes without auth check
  if (to.name === 'impersonate' || to.path.startsWith('/auth')) {
    next()
    return
  }

  const {
    isAgentContext,
    role: contextRole,
    token: contextToken,
  } = getAuthContext()

  // Auto-redirect routes based on context (agent vs admin)
  if (isAgentContext && to.path.startsWith('/admin/')) {
    // Agent trying to access /admin route → redirect to /agent equivalent
    const agentPath = to.path.replace(/^\/admin\//, '/agent/')
    next({ path: agentPath, query: to.query })
    return
  }

  if (!isAgentContext && to.path.startsWith('/agent/')) {
    // Agent route without agent session → require login (sessionStorage is tab-scoped)
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }

  const userRole = contextRole || 'admin'
  const token = contextToken
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)

  if (requiresAuth) {
    if (!token) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    const isAuthenticated = await getAuthenticatedAdmin(token)
    if (!isAuthenticated) {
      forceLogout()
      return
    }
  }

  if ((to.name === 'login' || to.name === 'signup') && token) {
    const isAuthenticated = await getAuthenticatedAdmin(token)
    if (isAuthenticated) {
      const redirectPath = isAgentContext
        ? '/agent/agents-management'
        : userRole === 'support'
          ? '/admin/chat-conversations'
          : '/admin/dashboard'
      next({ path: redirectPath })
      return
    }

    clearAuthSession()
    next()
    return
  }

  // Check role-based access
  if (to.meta.allowedRoles && Array.isArray(to.meta.allowedRoles)) {
    if (!to.meta.allowedRoles.includes(userRole)) {
      notify({
        message: 'You do not have permission to access this page.',
        color: 'danger',
        position: 'top-right',
        duration: 3000,
      })
      next(false)
      return
    }
  }

  next()
})

export default router
